/**
 * The API surface, transcribed from the FastAPI models on
 * `feat/collections-scoping-upload` — not from docs.
 *
 * Two things the shapes here encode that are easy to get wrong:
 *
 * - Every request model sets `extra="forbid"`, so an unknown field is a **422 with
 *   pydantic's list-shaped body**, not `{"detail": string}`. Two error shapes, always.
 * - `/query` is serialised with `exclude_unset`, so `grounding` is ABSENT (not null)
 *   unless `include_grounding` was true. Optional means optional.
 */

/**
 * THREE SHAPES, one list. `kind` discriminates, and it is the only field you may branch on
 * — a tool citation has `chunk_id: null`, so keying off the null is how you write a bug
 * that only shows up on portal answers.
 *
 * A `"chunk"` citation points into the index: it has a document, a chunk and offsets, and
 * the reader can open the passage. A `"tool"` citation is a LIVE read — a portal or a
 * spreadsheet — which is never embedded (ADR-0035), so there is no passage to open. Its
 * provenance keeps the invocation in `call`. Portal reads additionally carry the
 * discovered dataset/indicator identity, period, and public URL; `document_title` is the
 * ready-to-render source label. Older/non-portal tools fall back to the tool name.
 *
 * A `"history"` citation points into the CONVERSATION: it cites a prior turn by
 * `turn_index` and carries no document, chunk, or offsets. It never appears in the
 * flat `citations` list of a fresh `/query` response (it stays in `claim_map`),
 * but stored rows written before that contract still carry it — so every consumer
 * of stored citations must skip non-chunk/tool kinds instead of treating them as
 * documents.
 */
export interface Citation {
  kind: "chunk" | "tool" | "history";
  document_id: string | null;
  document_title: string;
  chunk_id: string | null;
  chunk_ordinal: number | null;
  page_number: number | null;
  section_path: string[];
  legal_reference: string | null;
  char_start: number | null;
  char_end: number | null;
  confidential?: boolean;
  owning_org_unit?: string | null;
  min_position_level?: number | null;
  /** `"tool"` only — the tool that read it, and the call that produced the figure. */
  tool?: string;
  call?: string;
  source_url?: string | null;
  source_title?: string | null;
  source_name?: string | null;
  source_id?: string | null;
  period?: string | null;
  retrieved_at?: string | null;
  source_version?: string | null;
  mcp_server?: string | null;
  mcp_tool?: string | null;
  /** Backend-authored, bounded metadata for a live MCP source. */
  source_metadata?: {
    url?: string | null;
    title?: string | null;
    name?: string | null;
    id?: string | null;
    period?: string | null;
    version?: string | null;
    retrieved_at?: string | null;
    mcp_server?: string | null;
    mcp_tool?: string | null;
  };
  /** `"history"` only — the cited prior turn's exchange ordinal. */
  turn_index?: number | null;
}

/** A stable per-citation key. `chunk_id` is null on a live read; `call` identifies it. */
export function citationKey(citation: Citation): string {
  if (citation.kind === "tool")
    return `tool:${citation.tool ?? ""}:${citation.call ?? ""}`;
  if (citation.kind === "history") return `history:${citation.turn_index ?? ""}`;
  return `chunk:${citation.chunk_id ?? ""}`;
}

export interface GroundingPassage {
  citation: Citation;
  text: string;
  score?: number | null;
}

/**
 * One citation key a published claim wrote, resolved to the exact passage it named.
 *
 * `citation_key` is the per-run `[P<n>]` label and means nothing outside its run;
 * `citation` is what survives — the same shape as `citations[]`, chunk id for an
 * indexed chunk, tool + call for a live read. `null` means the model invented the key:
 * link NOTHING for it, never guess.
 */
export interface ClaimCitationRef {
  citation_key: string;
  citation: Citation | null;
}

/**
 * One published claim and the passage(s) that support it — the AUTHORITATIVE
 * provenance (#532). A claim may rest on several passages; the list is never
 * flattened to one.
 */
export interface AnswerClaim {
  claim: string;
  citations: ClaimCitationRef[];
}


export interface QueryRequest {
  query: string;
  user_id: string;
  conversation_id?: string | null;
  collection_ids?: string[] | null;
  document_ids?: string[] | null;
  include_grounding?: boolean;
}

/** Backend publication reasons; see QueryResponse in src/aceh_rag/api/app.py. */
export type FallbackReason =
  | "over_refusal"
  | "no_claims"
  | "unverified_no_claims"
  | "unresolved_citation"
  | "coverage_incomplete"
  /** NOT a fallback (`fallback_used` is false): only the verified part is shown, with a
   *  note in the answer text naming what the documents could not answer. */
  | "partial_answer"
  | "unparseable_completion"
  | "no_answer"
  | "empty_completion"
  | (string & Record<never, never>);

/** Present on live answers; absent on messages stored before window reporting. */
export interface HistoryWindow {
  history_truncated?: boolean;
  history_exchanges_in_view?: number;
}

export interface QueryResponse extends HistoryWindow {
  answer: string;
  conversation_id: string;
  message_id: string;
  citations: Citation[];
  tool: string | null;
  tool_executed: string | null;
  fallback_used: boolean;
  /** How the answer was downgraded. Unknown additive values remain distinguishable. */
  fallback_reason: FallbackReason | null;
  dropped_claims_count: number;
  /** The backend proved the complete answer-to-provenance invariant. */
  provenance_complete: boolean;
  grounding?: GroundingPassage[];
  /** The claim-to-passage map. Optional because a turn REPLAYED from a message stored
   *  before #532 has none; the live `/query` response always carries it (possibly `[]`). */
  claim_map?: AnswerClaim[];
}

export interface Conversation {
  id: string;
  user_id: string;
  title: string | null;
  metadata: Record<string, unknown>;
}

export interface Message {
  id: string;
  role: "user" | "assistant" | string;
  content: string;
  source_citations: Citation[];
  metadata: Record<string, unknown>;
}

export interface ConversationDetail extends Conversation {
  messages: Message[];
}

export interface Collection {
  id: string;
  name: string;
  description: string | null;
  created_by: string | null;
  owning_org_unit: string | null;
  min_position_level: number | null;
  confidential: boolean;
  /** Counts DOCUMENT ROWS, so a queued-but-not-ingested upload counts 0. Deliberate:
   *  claiming "1 dokumen" for a file that cannot yet be retrieved would be a lie. */
  document_count: number;
}

/** `needs_review` joined the corpus vocabulary in migration 0020: a document
 *  whose every chunk quarantined is a finished ingest awaiting OCR review,
 *  not an in-flight `processing` one. */
export type DocumentStatus =
  | "pending"
  | "processing"
  | "indexed"
  | "needs_review"
  | "failed"
  | "archived";

export interface CorpusDocument {
  id: string;
  title: string;
  source_type: string;
  format: string | null;
  source_version: string | null;
  status: DocumentStatus;
  collection_id: string | null;
  /** The publication facts, normalised server-side across both ingest metadata shapes
   *  (`database/repository.py:document_bibliography`). ALL optional: the corpus was
   *  ingested before the catalogue backfill existed, so a document may record none of
   *  them, and a missing facet must render as unknown rather than as a default. */
  year: string | null;
  jenis: string | null;
  wilayah: string | null;
  /** INDEXED chunks only — what is actually retrievable. 0 means the document is in the
   *  collection but nothing of it can be answered from yet. */
  indexed_chunk_count: number;
  /** The citable public page on jdih.acehprov.go.id, not the file we happened to fetch. */
  source_url: string | null;
  owning_org_unit: string | null;
  min_position_level: number | null;
  confidential: boolean;
}

export interface CorpusPage {
  documents: CorpusDocument[];
  /** AFTER filters and the access predicate — "6 dari 1.204" means 1,204 this caller
   *  may see and that match, not 1,204 in the corpus. */
  total: number;
  limit: number;
  offset: number;
}

export interface QueryTrendCount {
  name: string;
  query_count: number;
  /** Share of all forward-tracked queries. Source shares may sum above 1 when one
   *  answer uses more than one source family. */
  share: number;
}

export interface QueryTrendKeyword {
  keyword: string;
  /** Number of distinct queries containing this normalized lexical term. */
  query_count: number;
}

export interface QueryTrendReport {
  tracked_queries: number;
  classified_queries: number;
  unclassified_queries: number;
  source_attributed_queries: number;
  categories: QueryTrendCount[];
  sources: QueryTrendCount[];
  keywords: QueryTrendKeyword[];
  tracking_started_at: string | null;
  tracking_through_at: string | null;
}

/** One indexed passage, fetched by chunk id — what makes a STORED citation readable. */
export interface ChunkText {
  chunk_id: string;
  document_id: string;
  document_title: string;
  text: string;
  page_number: number | null;
  section_path: string[];
  legal_reference: string | null;
}

/** A page of a document's indexed passages, in reading order. */
export interface DocumentChunkPage {
  chunks: ChunkText[];
  /** Every indexed chunk the document has — not the page size. */
  total: number;
  limit: number;
  offset: number;
}

export interface IngestionJob {
  job_id: string;
  status: string;
  documents?: Array<{
    document_id: string;
    filename: string;
    status: string;
    stored_document_id?: string | null;
    detail?: string | null;
    ocr_confidence?: number | null;
  }>;
  detail?: string | null;
}

// --------------------------------------------------------------- streaming --
/**
 * The SSE contract (`/query/stream`):
 *
 *   tool.started/tool.finished × N   (LIVE, while the agent works)
 *     → message.start → trace? → citation × N
 *     → (guard.blocked | passage × N then answer.delta × N then faithfulness) → done
 *
 * The tool events are LIVE — emitted as each tool runs, before the answer exists. The
 * ANSWER is still buffer-then-reveal: no model-authored answer text reaches the reader
 * until the Policy Guard has cleared it. Tool names, arguments and result summaries are
 * not the answer, which is why they may stream ahead of it.
 *
 * `message.start` is therefore NOT the first frame any more: the message id does not
 * exist until the answer is persisted, which is the last thing to happen. Key off event
 * names, never position.
 *
 * ONE CONTRACT COST: "no indexed content" cannot be known until the tools have run and
 * returned nothing, by which point the body is open — so it arrives as a terminal
 * `error` frame with code `http_404`, not an HTTP status. Failures raised BEFORE the
 * first tool (empty query, unknown conversation) still get real status codes.
 *
 * `passage` carries the TEXT behind each citation, so a claim can be checked against
 * the words the model was handed. It sits AFTER the guard branch — a blocked answer
 * ships none — and before the answer text.
 *
 * `faithfulness` lands AFTER the text on purpose. Once an answer is on screen it
 * cannot be unshown, so the verdict can only annotate. It carries the dropped-claim
 * COUNT and never the dropped sentences — those are pre-Policy-Guard model output.
 */
export type StreamFrame =
  | { event: "message.start"; data: { conversation_id: string; message_id: string } }
  | { event: "trace"; data: { tools: string[]; completed: boolean } }
  | {
      event: "tool.started";
      data: { call: number; tool: string; arguments: Record<string, unknown> };
    }
  | {
      event: "tool.finished";
      data: {
        call: number;
        tool: string;
        ok: boolean;
        ms: number;
        summary: string;
        passages?: number;
      };
    }
  | { event: "citation"; data: Citation }
  | {
      event: "passage";
      data:
        | {
            kind: "chunk";
            chunk_id: string;
            text: string;
            source_type?: string;
            score?: number;
          }
        | { kind: "tool_result"; tool: string; text: string; source_url: string | null };
    }
  | { event: "guard.blocked"; data: { reason: string } }
  | { event: "answer.delta"; data: { text: string } }
  | {
      event: "faithfulness";
      data: { dropped: number; fallback_used: boolean; fallback_reason: FallbackReason | null };
    }
  | { event: "done"; data: { finish_reason: string } & HistoryWindow }
  | { event: "error"; data: { code: string; message: string } };

export interface SessionUser {
  username: string;
  displayName: string;
  role: "admin" | "operator" | "user";
  subject: string;
  orgUnit: string | null;
  positionLevel: number | null;
}

export interface FacetValue {
  value: string;
  count: number;
}

/** The values a picker may filter by. Ordered by count descending, so the regions a
 *  caller actually works with surface before the alphabet decides. */
export interface CorpusFacets {
  years: FacetValue[];
  regions: FacetValue[];
}

// ------------------------------------------------------------------ notebooks

/** A pick that no longer resolves, or that moved when its document was re-ingested. */
export interface NotebookSourceIssue {
  document_id: string;
  /** `moved` — followed to a new row. `missing` — gone, or never visible to this caller;
   *  the two are deliberately indistinguishable server-side, because saying which would
   *  disclose that the document exists. */
  kind: "moved" | "missing";
  current_document_id: string | null;
}

/** A notebook with its sources RESOLVED against the corpus as it stands right now. */
export interface Notebook {
  id: string;
  title: string | null;
  whole_corpus: boolean;
  collection_ids: string[];
  document_ids: string[];
  excluded_document_ids: string[];
  /** Collections count as ONE source each — what the user added, not what it holds. */
  source_count: number;
  /** False means the composer must be disabled: an empty notebook, or one whose every
   *  pick has vanished, must NOT fall through to the whole corpus. */
  answerable: boolean;
  issues: NotebookSourceIssue[];
}
